import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Rocket, 
  ArrowRight, 
  CheckCircle, 
  PlayCircle, 
  BookOpen, 
  Zap,
  Target,
  Globe2,
  Brain,
  Award
} from 'lucide-react';
import { theme } from '../styles/theme';
import { BentoGridItem } from './BentoGrid';
import OnboardingSteps from './OnboardingSteps';
import PlatformInfoCards from './PlatformInfoCards';
import SupportContactCard from './SupportContactCard';
import { useTour } from '../context/TourContext';
import { onboardingTourSteps } from '../data/tourSteps';

const OnboardingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[8]};
`;

const WelcomeSection = styled.div`
  text-align: center;
  background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  color: white;
  padding: ${theme.spacing[12]} ${theme.spacing[8]};
  border-radius: ${theme.borderRadius.xl};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: pulse 4s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 0.3; }
  }
`;

const WelcomeContent = styled.div`
  position: relative;
  z-index: 1;
`;

const WelcomeTitle = styled.h1`
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: ${theme.spacing[4]};
  font-family: ${theme.typography.fontFamily.display};
  color: white;

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize['3xl']};
  }
`;

const WelcomeSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  margin-bottom: ${theme.spacing[8]};
  color: white;
  opacity: 1;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: ${theme.typography.lineHeight.relaxed};

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.base};
    margin-bottom: ${theme.spacing[6]};
  }
`;

const CTAButton = styled.button`
  background: white;
  color: ${theme.colors.primary[600]};
  border: none;
  padding: ${theme.spacing[4]} ${theme.spacing[8]};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  transition: all ${theme.transitions.base};
  box-shadow: ${theme.shadows.lg};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl};
    background: ${theme.colors.primary[50]};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing[4]};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: ${theme.spacing[6]};
  border-radius: ${theme.borderRadius.lg};
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatNumber = styled.div`
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: ${theme.spacing[2]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  opacity: 0.8;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[6]};
  }
`;

const OnboardingProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

interface OnboardingDashboardProps {
  onStartTour?: () => void;
}

const OnboardingDashboard: React.FC<OnboardingDashboardProps> = ({ onStartTour }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { startTour } = useTour();

  const platformStats = [
    { number: '10x', label: 'Faster Analysis', icon: <Zap size={20} /> },
    { number: '95%', label: 'Success Rate', icon: <Target size={20} /> },
    { number: '50+', label: 'Countries Served', icon: <Globe2 size={20} /> },
    { number: 'AI', label: 'Powered Insights', icon: <Brain size={20} /> }
  ];

  const handleStartInteractiveTour = () => {
    // Actually start the interactive tour instead of switching to dashboard
    startTour(onboardingTourSteps);
    // Don't call onStartTour which switches to dashboard view
    // The tour will handle showing the dashboard when it completes
  };

  return (
    <OnboardingContainer>
      {/* Welcome Hero Section */}
      <WelcomeSection>
        <WelcomeContent>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: theme.spacing[6] }}>
            <div style={{ 
              background: 'rgba(255,255,255,0.2)', 
              padding: theme.spacing[4], 
              borderRadius: theme.borderRadius.full,
              backdropFilter: 'blur(10px)'
            }}>
              <Rocket size={48} />
            </div>
          </div>
          
          <WelcomeTitle>Welcome to OpenTender AI</WelcomeTitle>
          <WelcomeSubtitle>
            Transform your tender process with AI-powered analysis, intelligent response generation, 
            and data-driven insights that help you win more contracts.
          </WelcomeSubtitle>
          
          <CTAButton onClick={handleStartInteractiveTour} className="start-analysis-button">
            <PlayCircle size={20} />
            Begin Interactive Tour
            <ArrowRight size={20} />
          </CTAButton>

          <StatsGrid>
            {platformStats.map((stat, index) => (
              <StatCard key={index}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: theme.spacing[3] }}>
                  {stat.icon}
                </div>
                <StatNumber>{stat.number}</StatNumber>
                <StatLabel>{stat.label}</StatLabel>
              </StatCard>
            ))}
          </StatsGrid>
        </WelcomeContent>
      </WelcomeSection>

      {/* Main Onboarding Content */}
      <MainContent>
        <OnboardingProgressSection>
          {/* Platform Information Cards */}
          <BentoGridItem
            title="Platform Overview"
            description="Discover what makes OpenTender AI the leading solution for tender analysis"
            icon={<Award size={24} />}
            className="platform-overview-section"
          >
            <PlatformInfoCards />
          </BentoGridItem>

          {/* Onboarding Steps */}
          <BentoGridItem
            title="Get Started in 6 Easy Steps"
            description="Follow our guided tour to master the platform"
            icon={<BookOpen size={24} />}
            className="onboarding-steps-container"
          >
            <OnboardingSteps 
              currentStep={currentStep} 
              onStepChange={setCurrentStep}
            />
          </BentoGridItem>
        </OnboardingProgressSection>

        {/* Support Section */}
        <div className="support-section">
          <SupportContactCard />
        </div>
      </MainContent>
    </OnboardingContainer>
  );
};

export default OnboardingDashboard;